import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../config/supabase';
import { useAdminCheck } from '../../hooks/useAdminCheck';
import { ArrowLeftIcon } from '../../components/icons/NavIcons';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageHeader from '../../components/PageHeader';

// Détection de l'environnement
const isLocal =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  window.location.hostname.endsWith(".app.github.dev");

// Nouvelle fonction pour déterminer l’URL (selon demande)
const getApiUrl = () => {
  const baseUrl = import.meta.env.VITE_FUNCTION_URL;
  return `${baseUrl}/api/TriggerJob`;
};
const apiUrl = getApiUrl();

const JobsManagementPage = () => {
  const { isAdmin, loading: adminLoading } = useAdminCheck();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [communesStats, setCommunesStats] = useState(null);
  const [urlsStats, setUrlsStats] = useState(null);
  const [simulationMode, setSimulationMode] = useState(false);
  const [simulationLoading, setSimulationLoading] = useState(false);
  const [batchMode, setBatchMode] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [functionKey, setFunctionKey] = useState(null);
  const [keyLoading, setKeyLoading] = useState(true);
  const [keyError, setKeyError] = useState(null);
  const [functionKeyMissing, setFunctionKeyMissing] = useState(false);

  // Récupération de la clé de fonction depuis Supabase (remplace useFunctionKey)
  useEffect(() => {
    const getFunctionKey = async () => {
      try {
        const keyName = isLocal ? 'FUNCTION_KEY_DEV' : 'FUNCTION_KEY';
        const { data, error } = await supabase
          .from('keys')
          .select('value')
          .eq('env', keyName)
          .single();
        if (error) {
          setKeyError(error.message);
          setFunctionKeyMissing(true);
        } else if (data) {
          setFunctionKey(data.value);
          setFunctionKeyMissing(false);
        } else {
          setFunctionKeyMissing(true);
        }
      } catch (err) {
        setKeyError(err.message);
        setFunctionKeyMissing(true);
      } finally {
        setKeyLoading(false);
      }
    };
    getFunctionKey();
  }, [isLocal]);

  // Fonction native pour formater les dates de façon relative
  const formatRelativeDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffSecs / 60);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      // Format selon la langue
      const lang = i18n.language || 'fr';
      
      if (diffDays > 30) {
        const month = date.toLocaleString(lang, { month: 'short' });
        const day = date.getDate();
        return `${day} ${month}`;
      } else if (diffDays > 0) {
        return t('time.daysAgo', { count: diffDays });
      } else if (diffHours > 0) {
        return t('time.hoursAgo', { count: diffHours });
      } else if (diffMins > 0) {
        return t('time.minutesAgo', { count: diffMins });
      } else {
        return t('time.justNow');
      }
    } catch (error) {
      return dateString;
    }
  };

  // Fonction pour charger les statistiques des communes
  const loadCommunesStats = async () => {
    try {
      const { data: communes, error } = await supabase
        .from('communes')
        .select('include, freshness')
        .eq('include', true);

      if (error) {
        console.error("Erreur lors de la récupération des stats communes:", error);
        return;
      }

      // Regrouper par freshness
      const statsByFreshness = communes.reduce((acc, commune) => {
        const raw = commune.freshness;
        const freshness = (raw === 0 || raw === '0') ? 'une seule fois' : (raw ?? 'Non défini');
        acc[freshness] = (acc[freshness] || 0) + 1;
        return acc;
      }, {});

      setCommunesStats({
        total: communes.length,
        byFreshness: statsByFreshness
      });
    } catch (error) {
      console.error("Erreur lors du chargement des stats communes:", error);
    }
  };

  // Fonction pour charger les statistiques des URLs
  const loadUrlsStats = async () => {
    try {
      const { data: urls, error } = await supabase
        .from('urls')
        .select('include, freshness')
        .eq('include', true);

      if (error) {
        console.error("Erreur lors de la récupération des stats URLs:", error);
        return;
      }

      // Regrouper par freshness
      const statsByFreshness = urls.reduce((acc, url) => {
        const raw = url.freshness;
        const freshness = (raw === 0 || raw === '0') ? 'une seule fois' : (raw ?? 'Non défini');
        acc[freshness] = (acc[freshness] || 0) + 1;
        return acc;
      }, {});

      setUrlsStats({
        total: urls.length,
        byFreshness: statsByFreshness
      });
    } catch (error) {
      console.error("Erreur lors du chargement des stats URLs:", error);
    }
  };

  // Fonction pour charger le mode simulation
  const loadSimulationMode = async () => {
    try {
      const { data, error } = await supabase
        .from('parameters')
        .select('value')
        .eq('parameter', 'simulation_ai')
        .maybeSingle();

      if (error) {
        console.error("Erreur lors de la récupération du mode simulation:", error);
        return;
      }

      setSimulationMode(data?.value === '1' || data?.value === 1 || data?.value === true);
    } catch (error) {
      console.error("Erreur lors du chargement du mode simulation:", error);
    }
  };

  // Fonction pour basculer le mode simulation
  const toggleSimulationMode = async () => {
    setSimulationLoading(true);
    try {
      const newValue = !simulationMode;
      const numericValue = newValue ? 1 : 0;
      
      // Mise à jour uniquement (pas d'insertion)
      const { error } = await supabase
        .from('parameters')
        .update({ value: numericValue.toString() })
        .eq('parameter', 'simulation_ai');

      if (error) throw error;

      setSimulationMode(newValue);
      showMessage(
        newValue 
          ? t('settings.admin.jobsManagement.simulationEnabled')
          : t('settings.admin.jobsManagement.simulationDisabled'),
        'success'
      );
    } catch (error) {
      console.error("Erreur lors de la mise à jour du mode simulation:", error);
      showMessage(t('settings.admin.jobsManagement.errorUpdatingSimulation'), 'error');
    } finally {
      setSimulationLoading(false);
    }
  };

  // Fonction pour charger le mode batch
  const loadBatchMode = async () => {
    try {
      const { data, error } = await supabase
        .from('parameters')
        .select('value')
        .eq('parameter', 'batch_ai')
        .maybeSingle();

      if (error) {
        console.error("Erreur lors de la récupération du mode batch:", error);
        return;
      }

      setBatchMode(data?.value === '1' || data?.value === 1 || data?.value === true);
    } catch (error) {
      console.error("Erreur lors du chargement du mode batch:", error);
    }
  };

  // Fonction pour basculer le mode batch
  const toggleBatchMode = async () => {
    setBatchLoading(true);
    try {
      const newValue = !batchMode;
      const numericValue = newValue ? 1 : 0;
      
      // Mise à jour uniquement (pas d'insertion)
      const { error } = await supabase
        .from('parameters')
        .update({ value: numericValue.toString() })
        .eq('parameter', 'batch_ai');

      if (error) throw error;

      setBatchMode(newValue);
      showMessage(
        newValue 
          ? t('settings.admin.jobsManagement.batchEnabled')
          : t('settings.admin.jobsManagement.batchDisabled'),
        'success'
      );
    } catch (error) {
      console.error("Erreur lors de la mise à jour du mode batch:", error);
      showMessage(t('settings.admin.jobsManagement.errorUpdatingBatch'), 'error');
    } finally {
      setBatchLoading(false);
    }
  };

  // Récupérer l'utilisateur connecté au chargement du composant
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) {
          console.error("Erreur lors de la récupération de l'utilisateur:", error);
        } else {
          setCurrentUser(user);
          if (user) {
            await loadJobs();
            await loadCommunesStats();
            await loadUrlsStats();
            await loadSimulationMode();
            await loadBatchMode();
          }
        }
      } catch (err) {
        console.error("Exception lors de la vérification de l'authentification:", err);
      } finally {
        setAuthChecked(true);
      }
    };
    
    getUser();
  }, []);

  // Fonction pour charger les jobs
  const loadJobs = async () => {
    try {
      setLoading(true);
      
      // Récupérer tous les jobs (sans filtre user_id)
      const { data: allJobs, error: allJobsError } = await supabase
        .from('import_jobs')
        .select('id, source, status, created_at, result, error_message, prompt, model')
        .order('created_at', { ascending: false });

      if (allJobsError) {
        throw new Error(allJobsError.message);
      }

      setJobs(allJobs || []);

    } catch (error) {
      console.error("Erreur lors de la récupération des jobs:", error);
      showMessage(t('settings.admin.jobsManagement.errorFetchingJobs'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Supprimer un job
  const deleteJob = async (jobId) => {
    try {
      const { error } = await supabase
        .from('import_jobs')
        .delete()
        .eq('id', jobId);
      
      if (error) throw error;
      
      // Mettre à jour la liste des jobs
      setJobs(jobs.filter(job => job.id !== jobId));
      showMessage(t('settings.admin.jobsManagement.jobDeleted'), 'success');
    } catch (error) {
      console.error("Erreur lors de la suppression du job:", error);
      showMessage(t('settings.admin.jobsManagement.errorDeletingJob'), 'error');
    } finally {
      setDeleteConfirmation(null);
    }
  };

  // Relancer un job
  const restartJob = async (job) => {
    try {
      // Toujours commencer par 'pending' - le système déterminera automatiquement
      // s'il faut passer à l'étape 2 lors du traitement
      const initialStatus = 'pending';
      
      // Créer une copie du job avec le statut approprié (sans user_id)
      const { data, error } = await supabase
        .from('import_jobs')
        .insert({
          source: job.source,
          prompt: job.prompt,
          status: initialStatus,
          model: job.model
        })
        .select()
        .single();
      
      if (error) throw error;
      
      // Ajouter le nouveau job à la liste
      setJobs([data, ...jobs]);
      showMessage(t('settings.admin.jobsManagement.jobRestarted'), 'success');
    } catch (error) {
      console.error("Erreur lors du redémarrage du job:", error);
      showMessage(t('settings.admin.jobsManagement.errorRestartingJob'), 'error');
    }
  };

  // Fonction pour déclencher l'exécution des jobs
  const triggerJobExecution = async () => {
    setTriggerLoading(true);
    try {
      console.log('🎯 Déclenchement du traitement des tâches d\'importation...');

      if (!functionKey) {
        throw new Error('La clé de fonction n\'est pas disponible');
      }
      
      // Appeler la fonction Azure TriggerJob
      const triggerResponse = await fetch(apiUrl, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "x-functions-key": functionKey
        },
        body: JSON.stringify({})
      });

      const triggerResult = await triggerResponse.json();
      
      if (triggerResponse.ok && triggerResult.success) {
        let message = t('settings.admin.jobsManagement.jobsTriggered', { 
          count: triggerResult.jobsTriggered || 0
        });
        
        showMessage(message, 'success');
        
        // Recharger les jobs après déclenchement
        await loadJobs();
        
      } else {
        throw new Error(triggerResult.error || triggerResult.message || 'Erreur lors du déclenchement');
      }

    } catch (error) {
      console.error("Erreur lors du déclenchement des jobs:", error);
      showMessage(`${t('settings.admin.jobsManagement.errorTriggeringJobs')}: ${error.message}`, 'error');
    } finally {
      setTriggerLoading(false);
    }
  };

  // Afficher un message temporaire
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  // Si l'état de l'authentification n'est pas encore vérifié, afficher un indicateur de chargement
  if (adminLoading || !authChecked || keyLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAdmin) {
    return null;
  }

  const pendingJobsCount = jobs.filter(job => job.status === 'pending' || job.status === 'pending_second_step').length;
  const processingJobsCount = jobs.filter(job => job.status === 'processing').length;
  const completedJobsCount = jobs.filter(job => job.status === 'completed').length;

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <PageHeader title={t('settings.admin.jobsManagement.title')} backPath="/settings" />

      <div className="p-4 max-w-full overflow-hidden">
        {/* Messages */}
        {message.text && (
          <div className={`mb-4 p-3 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-800' :
            message.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
            message.type === 'error' ? 'bg-red-100 text-red-800' :
            'bg-blue-100 text-blue-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Avertissement si la clé est manquante */}
        {functionKeyMissing && (
          <div className="mb-4 p-3 rounded-lg bg-yellow-100 text-yellow-800 border border-yellow-300">
            <div className="flex items-start gap-2">
              <span className="text-lg">⚠️</span>
              <div>
                <p className="font-medium">Configuration requise</p>
                <p className="text-sm mt-1">
                  {keyError || 'La clé de fonction n\'a pas pu être récupérée depuis la base de données.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {currentUser && (
          <div className="flex justify-end gap-4 mb-6">
            <button
              onClick={() => loadJobs()}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              🔄 {t('common.refresh')}
            </button>
            <button
              onClick={triggerJobExecution}
              disabled={triggerLoading}
              className="bg-[#2894D8] text-white px-4 py-2 rounded-lg hover:bg-[#136E9D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {triggerLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {t('settings.admin.jobsManagement.triggering')}
                </>
              ) : (
                <>
                  <span>🚀</span>
                  {t('settings.admin.jobsManagement.triggerJobs')}
                </>
              )}
            </button>
          </div>
        )}

        {/* Switch Mode Simulation */}
        {currentUser && (
          <div className={`mb-4 p-4 bg-gray-50 rounded-lg border-2 ${
            simulationMode ? 'border-yellow-400' : 'border-red-400'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-h3 text-gray-700">
                  {t('settings.admin.jobsManagement.simulationMode')}
                </span>
                {simulationMode ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {t('settings.admin.jobsManagement.simulation')}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {t('settings.admin.jobsManagement.production')}
                  </span>
                )}
              </div>
              <button
                onClick={toggleSimulationMode}
                disabled={simulationLoading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#2894D8] focus:ring-offset-2 disabled:opacity-50 ${
                  simulationMode ? 'bg-yellow-400' : 'bg-red-400'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    simulationMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {/* Switch Mode Batch */}
        {currentUser && (
          <div className={`mb-4 p-4 bg-gray-50 rounded-lg border-2 ${
            batchMode ? 'border-green-400' : 'border-orange-400'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-h3 text-gray-700">
                  {t('settings.admin.jobsManagement.batchMode')}
                </span>
                {batchMode ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {t('settings.admin.jobsManagement.batchActive')}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    {t('settings.admin.jobsManagement.batchInactive')}
                  </span>
                )}
              </div>
              <button
                onClick={toggleBatchMode}
                disabled={batchLoading}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#2894D8] focus:ring-offset-2 disabled:opacity-50 ${
                  batchMode ? 'bg-green-500' : 'bg-orange-400'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    batchMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        )}

        {/* Statistiques des communes avec include=Yes */}
        {communesStats && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h3 className="text-h3 text-gray-700 mb-2">
              📊 {communesStats.total} {t('settings.admin.jobsManagement.communesToInclude')}
            </h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(communesStats.byFreshness)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([freshness, count]) => (
                  <div key={freshness} className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">{freshness} </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {count}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* Statistiques des URLs avec include=Yes */}
        {urlsStats && (
          <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="text-h3 text-gray-700 mb-2">
              🔗 {urlsStats.total} {t('settings.admin.jobsManagement.urlsToInclude')}
            </h3>
            <div className="flex flex-wrap gap-3">
              {Object.entries(urlsStats.byFreshness)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([freshness, count]) => (
                  <div key={freshness} className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">{freshness} </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {count}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        )}
        
        {/* Statistiques des jobs */}
        {jobs.length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                {t('settings.admin.jobsManagement.totalJobs')}: {jobs.length}
              </span>
              <div className="flex items-center gap-4">
                <span className={pendingJobsCount > 0 ? 'text-blue-600 font-medium' : ''}>
                  {t('settings.admin.jobsManagement.pendingJobs')}: {pendingJobsCount}
                </span>
                {processingJobsCount > 0 && (
                  <span className="text-yellow-600 font-medium">
                    En traitement: {processingJobsCount}
                  </span>
                )}
                {completedJobsCount > 0 && (
                  <span className="text-green-600">
                    Terminés: {completedJobsCount}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 border-b">
            <h2 className="text-h2">{t('settings.admin.jobsManagement.title')}</h2>
          </div>
          {loading ? (
            <LoadingSpinner message={t('common.loading')} />
          ) : jobs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t('settings.admin.jobsManagement.noJobs')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-[25%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.jobsManagement.source')}
                    </th>
                    <th className="w-[12%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.jobsManagement.status')}
                    </th>
                    <th className="w-[13%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.jobsManagement.created')}
                    </th>
                    <th className="w-[35%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.jobsManagement.results')}
                    </th>
                    <th className="w-[15%] px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('settings.admin.jobsManagement.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {jobs.map((job) => (
                    <tr key={job.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm truncate" title={job.source}>
                        {job.source}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${job.status === 'completed' ? 'bg-green-100 text-green-800' : 
                          job.status === 'failed' ? 'bg-red-100 text-red-800' : 
                          job.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                          (job.status === 'pending' || job.status === 'pending_second_step') ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'}`}>
                          {t(`settings.admin.importCommune.status.${job.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatRelativeDate(job.created_at)}
                      </td>
                      <td className="px-4 py-4 text-sm overflow-hidden">
                        {job.result ? (
                          <div className="text-green-600 font-medium min-w-0">
                            <div className="truncate" title={job.result}>
                              {job.result}
                            </div>
                          </div>
                        ) : job.prompt ? (
                          <div className="text-gray-600 min-w-0">
                            <div className="truncate" title={job.prompt}>
                              {job.prompt}
                            </div>
                          </div>
                        ) : null}
                        {job.status === 'failed' && (
                          <span className="text-red-600 text-xs">
                            {job.error_message || t('settings.admin.jobsManagement.error')}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        {job.status !== 'pending' && job.status !== 'pending_second_step' && (
                          <button
                            onClick={() => restartJob(job)}
                            className="text-[#2894D8] hover:text-[#136E9D]"
                          >
                            {t('settings.admin.jobsManagement.restart')}
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteConfirmation(job.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('common.delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {deleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-h2 mb-4">{t('settings.admin.jobsManagement.confirmDelete')}</h3>
            <p className="text-gray-600 mb-6">{t('settings.admin.jobsManagement.confirmDeleteText')}</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmation(null)}
                className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => deleteJob(deleteConfirmation)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsManagementPage;
